<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <button
      v-if="minimized"
      class="btn btn-primary ai-chat-button d-flex justify-content-center align-items-center"
      @click="toggleChat"
    >
      <i class="bi bi-chat-fill me-3"></i>
      <span>
        {{ $t('resultsmessages.title') }}
      </span>
    </button>
    <div v-else>
      <div class="results-messages d-flex">
        <div
          v-if="showStoredMessages"
          class="stored-messages card d-flex flex-column"
        >
          <div
            v-if="storedMessages.length"
            class="d-flex justify-content-center align-items-center mt-7 mb-3"
          >
            <h5>{{ $t('resultsmessages.historyTitle') }}</h5>
          </div>

          <div
            v-if="messages.length"
            class="d-flex justify-content-center align-items-center mt-3 mb-3"
          >
            <button
              style="cursor: pointer"
              class="btn btn-secondary btn-sm"
              :disabled="waitingForResponse"
              @click="didSelectNewMessage"
            >
              <div class="d-flex align-items-center justify-content-center">
                <i class="bi bi-plus-square me-2"></i>
                <span>{{ $t('resultsmessages.newMessage') }}</span>
              </div>
            </button>
          </div>
          <div class="list-group ms-3 me-3 mt-3 mb-3">
            <div
              v-for="message in storedMessages"
              :key="message.id"
              href="#"
              class="list-group-item list-group-item-action flex-column align-items-start"
              :class="{
                'list-group-item-secondary':
                  selectedStoredMessage === message.id
              }"
              style="cursor: pointer"
              @click="didSelectStoredMessage(message)"
            >
              <div class="d-flex w-100 justify-content-between">
                <span class="mb-1">{{ message.titleForHistory }}</span>
              </div>
              <div>
                <span class="mb-1 text-small text-muted">
                  {{ message.formattedDate }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="storedMessages.length"
            class="card-footer d-flex justify-content-center mt-auto"
          >
            <button
              class="btn btn-secondary btn-sm"
              :disabled="waitingForResponse"
              @click="didSelectClearHistory"
            >
              <div class="d-flex align-items-center">
                <i class="bi bi-trash me-2"></i>
                <span>
                  {{ $t('resultsmessages.clearHistory') }}
                </span>
              </div>
            </button>
          </div>
        </div>
        <div class="inner-results-messages" :style="chatWindowStyle">
          <div class="chat-window">
            <div class="chat-header">
              <h3 v-if="debug" class="text-muted">{{ modelName }}</h3>
              <button
                v-if="messages.length || storedMessages.length"
                class="btn btn-link chat-header-button"
                @click="toggleStoredMessages"
              >
                <i class="bi bi-list"></i>
              </button>
              <div v-else></div>
              <h5 class="mb-0">{{ $t('resultsmessages.title') }}</h5>
              <button
                class="btn btn-link minimize-button chat-header-button"
                @click="toggleChat"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div
              v-if="messages.length === 0 && !notEnoughResults"
              class="disclaimer d-flex mt-3 justify-content-center"
            >
              <div class="d-flex align-items-center">
                <i class="bi bi-exclamation-triangle-fill text-info me-2"></i>
                <span class="text-info">
                  {{ $t('resultsmessages.disclaimer') }}
                </span>
              </div>
            </div>
            <div
              v-if="messages.length === 0 && !notEnoughResults"
              class="disclaimer d-flex mt-3 justify-content-center"
            >
              <div class="d-flex align-items-center">
                <a :href="$t('resultsmessages.moreInfoLink')" target="_blank">
                  {{ $t('resultsmessages.moreInfo') }}
                  <i class="bi bi-box-arrow-up-right ms-2"></i>
                </a>
              </div>
            </div>
            <div class="chat-body">
              <h2
                v-if="selectedModelName === 'compare' && messagesForChat.length"
              >
                {{ messages[messages.length - 1].modelName }}
              </h2>
              <div
                v-for="message in messagesForChat"
                :key="message.unixEpochTime"
                class="message"
                :class="message.direction"
              >
                <div class="message-text">
                  <div
                    v-if="message.text"
                    v-html="markdownToHtml(message.text)"
                  ></div>
                  <div v-else>bad data</div>
                </div>
                <span
                  class="message-timestamp mt-2"
                  :class="message.direction === 'out' ? 'me-3' : 'ms-3'"
                >
                  {{ formatTimestamp(message.timestamp) }}
                </span>
              </div>
              <div v-if="loading" class="spinner-border" role="status"></div>
              <div
                v-if="waitingForResponse && !loading && !maxWaitingPassed"
                class="spinner-grow"
                role="status"
              ></div>
              <div v-if="maxWaitingPassed || error" class="text-danger mt-5">
                <p>
                  {{ $t('resultsmessages.error.generic') }}
                </p>
                <button class="btn btn-primary btn-sm mt-3" @click="reload">
                  {{ $t('resultsmessages.reload') }}
                </button>
              </div>
            </div>
            <div v-if="compareMessages.length" class="chat-body">
              <h2>
                {{ compareMessages[compareMessages.length - 1].modelName }}
              </h2>

              <div
                v-for="message in compareMessages"
                :key="message.unixEpochTime + 'compare'"
                class="message"
                :class="message.direction"
              >
                <div class="message-text">
                  <div
                    v-if="message.text"
                    v-html="markdownToHtml(message.text)"
                  ></div>
                  <div v-else>bad data</div>
                </div>
                <span
                  class="message-timestamp mt-2"
                  :class="message.direction === 'out' ? 'me-3' : 'ms-3'"
                >
                  {{ formatTimestamp(message.timestamp) }}
                </span>
              </div>
            </div>

            <div
              v-if="notEnoughResults"
              class="text-info mb-5 d-flex justify-content-center ms-5 me-5"
            >
              {{ $t('resultsmessages.error.notEnoughResults') }}
            </div>
            <!-- Pre-canned prpoposals-->
            <div v-if="messages.length === 0 && !notEnoughResults">
              <div
                class="suggestions-header mb-3 d-flex justify-content-center"
              >
                <span class="text-muted">
                  {{ $t('resultsmessages.suggestionHeader') }}
                </span>
              </div>
              <div class="suggestions-body pb-5 d-flex justify-content-center">
                <button
                  v-for="suggestionText in suggestionTexts"
                  :key="suggestionText"
                  class="btn btn-secondary ms-5 me-5"
                  @click="didSelectSuggestion(suggestionText)"
                >
                  {{ suggestionText }}
                </button>
              </div>
            </div>

            <!-- Prpoosals from AI -->
            <div v-if="messages.length > 0 && proposalsFromAI.length">
              <div
                class="suggestions-header mb-3 d-flex justify-content-center"
              >
                <span class="text-muted">
                  {{ $t('resultsmessages.suggestionHeader') }}
                </span>
              </div>
              <div class="suggestions-body pb-5 d-flex justify-content-center">
                <button
                  v-for="suggestionText in proposalsFromAI"
                  :key="suggestionText"
                  class="btn btn-secondary ms-5 me-5"
                  @click="didSelectSuggestion(suggestionText)"
                >
                  {{ suggestionText }}
                </button>
              </div>
            </div>

            <div class="chat-footer">
              <input
                v-model="newMessage"
                :disabled="notEnoughResults"
                type="text"
                class="form-control"
                :placeholder="$t('resultsmessages.placeholder')"
                @keyup.enter="sendMessage"
              />
              <button
                class="btn btn-primary"
                :disabled="sendButtonDisabled"
                @click="sendMessage"
              >
                {{ $t('resultsmessages.send') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
import { useRealtime } from './realtime'

export default {
  name: 'ResultsMessages',
  components: {},
  props: {
    actions: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    questions: {
      type: Object,
      default: () => {}
    },
    trendCategories: {
      type: Object,
      default: () => {}
    },
    trendTags: {
      type: Object,
      default: () => {}
    },
    trendQuestions: {
      type: Object,
      default: () => {}
    },
    surveyName: {
      type: String,
      default: ''
    },
    organizationName: {
      type: String,
      default: ''
    },
    organizationFullName: {
      type: String,
      default: ''
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    selectedAttributeSegment: {
      type: Object,
      default: () => {}
    },
    section: {
      type: String,
      default: 'overview'
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const realtime = useRealtime(props)

    return {
      realtime
    }
  },
  data() {
    return {
      aiChatWindowLastSeenDate: localStorage.getItem(
        'aiChatWindowLastSeenDate'
      ),
      newMessage: '',
      minimized: true,
      scrollTimer: null,
      waitingTimer: null,
      secondsWaiting: 0,
      maxSecondsWaiting: 15,
      maxWaitingPassed: false,
      selectedModelName: this.debug ? 'compare' : 'bedrock',
      showStoredMessages: false,
      selectedStoredMessage: null,
      storedMessages: [],
      currentSessionId: null // Track the current session being viewed
    }
  },
  computed: {
    dataInResponse() {
      return this.realtime.dataInResponse
    },
    proposalsFromAI() {
      return this.realtime.proposalsFromAI || []
    },
    suggestionTexts() {
      return this.realtime.suggestionTexts(this.$t) || []
    },
    notEnoughResults() {
      return this.realtime.notEnoughResults
    },
    sendButtonDisabled() {
      const hasSentCompareMessage =
        this.selectedModelName === 'compare' && this.messages.length > 0
      return (
        hasSentCompareMessage ||
        this.newMessage.trim() === '' ||
        this.waitingForResponse ||
        this.notEnoughResults
      )
    },
    messages() {
      const modelsFound = Object.keys(this.realtime.messages)
      if (!modelsFound.length) {
        return []
      }

      return this.realtime.messages[modelsFound[modelsFound.length - 1]].map(
        (m) => {
          const { text, data } = this.realtime.extractDataFromMessage(m)
          return {
            ...m,
            text,
            data
          }
        }
      )
    },
    messagesForChat() {
      return this.messages.filter((m) => {
        return !m.tags || !m.tags.includes('results-summary')
      })
    },
    compareMessages() {
      if (this.selectedModelName === 'compare') {
        const modelsFound = Object.keys(this.realtime.messages).filter(
          (name) => name !== 'compare'
        )
        if (modelsFound.length > 1) {
          return this.realtime.messages[modelsFound[0]]
        } else {
          return []
        }
      } else {
        return []
      }
    },
    modelName() {
      if (this.selectedModelName === 'compare') {
        const modelsFound = Object.keys(this.realtime.messages)
        if (modelsFound.length) {
          return modelsFound[modelsFound.length - 1]
        } else {
          return this.selectedModelName
        }
      } else {
        return this.selectedModelName
      }
    },
    error() {
      return this.realtime.error
    },
    loading() {
      return this.realtime.loading
    },
    waitingForResponse() {
      return this.realtime.waitingForResponse
    },
    waitingForResponseSince() {
      return this.realtime.waitingForResponseSince
    },
    organizationFeatures() {
      return this.realtime.organizationFeatures
    },

    chatWindowStyle() {
      if (this.isOnMobile()) {
        return {
          width: '100vw',
          maxWidth: '100vw'
        }
      }

      const width =
        this.selectedModelName === 'compare'
          ? 'clamp(640px, 60vw, 960px)'
          : 'clamp(360px, 32vw, 520px)'

      return {
        width,
        maxWidth: width
      }
    }
  },
  watch: {
    section(newSection) {
      this.realtime.setSection({ section: newSection })
      this.updateStoredMessages()
    },
    messages(newMessages) {
      if (newMessages && newMessages.length > 0) {
        if (this.scrollTimer) {
          this.scrollToBottom()
          clearTimeout(this.scrollTimer)
        }
        this.scrollTimer = setTimeout(() => {
          this.scrollToBottom()
        }, 200)
        this.updateStoredMessages()
      }
    }
  },
  mounted() {
    this.realtime.clear()

    this.realtime.setSection({ section: this.section })
    if (!this.aiChatWindowLastSeenDate && !this.isOnMobile()) {
      // open window automatically if its the first time user sees the button
      this.toggleChat()
    }
    this.updateStoredMessages()
  },
  methods: {
    didSelectStoredMessage(message) {
      this.realtime.loadStoredMessagesFromSession({
        sessionId: message.id,
        section: this.section
      })
      this.showStoredMessages = false
      this.selectedStoredMessage = message.id
      this.currentSessionId = message.id
    },
    didSelectNewMessage() {
      this.updateStoredMessages()
      this.realtime.clear()
      this.newMessage = ''
      this.showStoredMessages = false
      this.scrollToBottom()
      this.selectedStoredMessage = null
      this.currentSessionId = null
    },
    didSelectSuggestion(text) {
      this.newMessage = text
      this.sendMessage()
    },
    didSelectClearHistory(message) {
      this.realtime.clearAllStoredMessages()
      this.updateStoredMessages()
      this.showStoredMessages = false
      this.selectedStoredMessage = null
    },
    handleProposalFromWidget(data) {
      // data can be a string (for backward compatibility) or an object with proposal and sessionId
      const proposal = typeof data === 'string' ? data : data.proposal
      const sessionId = typeof data === 'object' ? data.sessionId : null

      // If sessionId is provided, load that session first
      if (sessionId) {
        this.realtime.loadStoredMessagesFromSession({
          sessionId: sessionId,
          section: this.section
        })
        this.selectedStoredMessage = sessionId
        this.currentSessionId = sessionId
        this.showStoredMessages = false

        // Give a small delay to ensure messages are loaded before sending
        this.$nextTick(() => {
          // Open the chat window and set the message
          if (this.minimized) {
            this.toggleChat()
          }

          // Set message and send
          this.newMessage = proposal
          this.$nextTick(() => {
            this.sendMessage()
          })
        })
      } else {
        // Open the chat window and set the message
        if (this.minimized) {
          this.toggleChat()
        }

        // Set message and send
        this.newMessage = proposal
        this.$nextTick(() => {
          this.sendMessage()
        })
      }
    },
    formatTimestamp(timestamp) {
      return this.realtime.formatTimestamp(timestamp)
    },
    isOnMobile() {
      return window.innerWidth <= 767
    },
    markdownToHtml(text) {
      const md = new MarkdownIt()
      return md.render(text)
    },
    toggleChat() {
      if (this.minimized) {
        localStorage.setItem('aiChatWindowLastSeenDate', new Date())
      }
      this.minimized = !this.minimized
    },
    toggleStoredMessages() {
      this.showStoredMessages = !this.showStoredMessages
    },
    sendMessage() {
      this.maxWaitingPassed = false
      this.secondsWaiting = 0
      const date = new Date()
      const unixEpochTime = Math.floor(date.getTime() / 1000)

      // Use all messages (including filtered ones) for order calculation
      const allMessages = this.messages
      const userMessage = {
        direction: 'out',
        text: this.newMessage,
        timestamp: date,
        unixEpochTime,
        order: allMessages.length + 1,
        modelName: this.modelName
      }

      // If we have a specific session ID (from loading a stored session or proposal),
      // use the session-specific send method to ensure messages go to that session
      if (this.currentSessionId) {
        this.realtime.sendRealtimeMessageActionWithSessionId({
          sessionId: this.currentSessionId,
          message: userMessage,
          surveyName: this.surveyName,
          groupName:
            this.section === 'groupsComparison'
              ? 'all'
              : this.selectedGroup.name,
          aggregate: this.realtime.surveyIsAggregate,
          surveyData: this.realtime.surveyData
        })
      } else {
        // Otherwise, use the normal send method
        this.realtime.sendRealtimeMessageAction({
          message: userMessage,
          surveyName: this.surveyName,
          groupName:
            this.section === 'groupsComparison'
              ? 'all'
              : this.selectedGroup.name,
          aggregate: this.realtime.surveyIsAggregate,
          section: this.section,
          surveyData: this.realtime.surveyData
        })
      }

      this.newMessage = ''
      // contiously update the secondsWaiting unil waitingForResponse is false
      this.waitingTimer = setInterval(() => {
        if (this.waitingForResponse) {
          this.secondsWaiting = this.realtime.secondsWaitingForResponse()
          if (this.secondsWaiting > this.maxSecondsWaiting) {
            this.maxWaitingPassed = true
            this.secondsWaiting = 0
            clearInterval(this.waitingTimer)
          }
        } else {
          this.secondsWaiting = 0
          clearInterval(this.waitingTimer)
        }
      }, 1000)
      this.scrollToBottom()
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatBody = this.$el.querySelector('.chat-body')
        if (chatBody) {
          chatBody.scrollTo({
            top: chatBody.scrollHeight
            //     behavior: 'smooth' // results in a weird scroll behavior
          })
        }
      })
    },
    updateStoredMessages() {
      this.storedMessages = this.realtime.getStoredMessages().map((m) => {
        const titleForHistory = m.id.includes('resultssummary')
          ? this.$t('widgetResultsMessages.historyTitle')
          : m.title
        return {
          titleForHistory,
          ...m
        }
      })
    },
    reload() {
      window.location.reload()
    }
  }
}
</script>

<style lang="scss">
.message-text {
  p:last-child {
    margin-bottom: 0px !important;
  }
}
</style>

<style lang="scss" scoped>
.ai-chat-button {
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
}

.inner-results-messages {
  height: 100%;
}

.results-messages {
  position: fixed;
  bottom: 0;
  right: 0;
  // width: 600px;
  height: 800px;
  max-height: 100%;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border: 1px solid var(--kt-secondary);
  backdrop-filter: blur(5px);
  border-radius: 5px;
  background: var(--kt-card-bg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.stored-messages {
  min-width: 200px;
  border-radius: 0%;
  border-left: none;
  border-top: none;
  border-bottom: none;
  border-right: 1px solid var(--kt-secondary);
  display: flex;
  flex-direction: column;
}

.stored-messages .list-group {
  overflow-y: auto;
  flex-grow: 1;
}

/* On mobile, we override .results-messages to set width and height to 100% */
@media (max-width: 767px) {
  .results-messages {
    // width: 100%;
    height: 100%;
    left: 0;
    right: 0;
    max-width: 100vw;
  }

  .stored-messages {
    position: absolute;
    top: 60px;
    height: calc(100% - 60px);
    display: flex;
    flex-direction: column;
  }
}

.chat-window {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.chat-header {
  background: var(--kt-secondary);
  color: #fff;
  padding: 10px;
  border-bottom: 1px solid #ccc;
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.minimize-button {
  color: #fff;
}

.chat-header-button > i {
  font-size: 1.4rem !important;
}
.chat-body {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
}
.message {
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.message.out {
  align-items: flex-end;
}
.message.in {
  align-items: flex-start;
}
.message-text {
  background: var(--kt-secondary) !important;
  padding: 8px 10px;
  border-radius: 15px;
  width: fit-content;
  max-width: min(32rem, 100%);
  word-break: break-word;

  p {
    margin-bottom: 0px !important;
  }
}
.message.out .message-text {
  background: var(--kt-info-light) !important;
  // background: #b6d6f8 !important;
}
.message-timestamp {
  font-size: 0.75em;
  color: #666;
}
.chat-footer {
  display: flex;
  padding: 10px;
  border-top: 1px solid var(--kt-secondary);
  background: var(--kt-secondary);
}
.chat-footer input {
  flex: 1;
  margin-right: 10px;
}

// button

.ai-chat-button {
  background: linear-gradient(45deg, #0064e7, #b3783e, #f4c032);
  background-size: 400% 400%;
  color: white; /* Contrasting text color */
  padding: 1rem 1.5rem; /* Increase size */
  font-size: 1.2rem; /* Increase font size */
  border: none; /* Remove default border */
  border-radius: 8px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Soft shadow */
  position: fixed; /* Ensure button stays in place */
  bottom: 20px; /* Adjust as needed */
  right: 20px; /* Adjust as needed */
  // animation: pulse 2s infinite, rainbow 10s infinite linear; /* Add pulse and rainbow animation */
  animation: rainbow 7s infinite linear; /* Add pulse and rainbow animation */
  // text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7); /* Add black text shadow */
}
.ai-chat-button:hover {
  animation: pulse 2s infinite; /* Add pulse and rainbow animation */
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
}

@keyframes rainbow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.ai-chat-button:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3); /* Enhanced shadow on hover */
}
</style>
